import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { GearIcon } from '@common_components/icons/gear.icon';
import { ChartIcon } from '@common_components/icons/chart.icon';
import { ChevronsRightIcon } from '@common_components/icons/chevronsRight.icon';
import { MagnifyingGlassMinusIcon } from '@common_components/icons/magnifyingGlassMinus.icon';
import { MagnifyingGlassPlusIcon } from '@common_components/icons/magnifyingGlassPlus.icon';
import { ChevronsLeftIcon } from '@common_components/icons/chevronsLeft.icon';
import { EventManagerIcon } from '@common_components/icons/eventManager.icon';
import { EyeIcon } from '@common_components/icons/eye.icon';

import { KEY } from 'constants/local_storage_key.constant';

import { EventManagerNavigationBarSkeleton } from './eventManagerNavigationBarSkeleton.component';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import TimerButton from 'components/buttons/TimerButton';

import { useEventManagerNavigationBar } from '../hooks/useEventManagerNavigationBar.hooks';

import { EventManager, EventManagerWithPosition } from 'interfaces/eventManager.interface';

import { ArchiveBoxIcon, Button, CommonGlobalService, dayjs, HNBBreadcrumbItem, UUID, EventManagerViewIcon } from '@umanforge/common';
import { useDispatch } from 'react-redux';
import { clearVisualisationToken } from '@umanforge/common/dist/store/slices/visualisation_token.slices';
import { DisconnectIcon } from '@common_components/icons/disconnect.icon';
import ActivationVisualisationModal from 'components/modals/activation_visualisation_modal.component';


interface EventManagerNavigationBarProps {
  eventManager: EventManager | null;
  onEventManagersChanged: ( eventManagers: EventManagerWithPosition[] ) => void;
  eventManagerId:UUID | undefined;
  setEventManagerId: ( value: UUID | undefined ) => void;
  isViewMode: boolean;
  isArchived: boolean;
  setIsArchived: React.Dispatch<React.SetStateAction<boolean>>;
  calendarItem: number
  setCalendarItem: React.Dispatch<React.SetStateAction<number>>;
  days: dayjs.Dayjs[];
  setDays: React.Dispatch<React.SetStateAction<dayjs.Dayjs[]>>;
  setNewDays: ( date: dayjs.Dayjs ) => void;
  isAdminOfEventManager: boolean;
  refreshEventManager: () => Promise<void>;
  setSelectedSectorId: ( newSectorId: UUID | undefined ) => void;
  showCharts: boolean;
  onToggleCharts: () => void;
}

export const EventManagerNavigationBar: React.FC<EventManagerNavigationBarProps> = ( {
  eventManager, onEventManagersChanged,
  eventManagerId, setEventManagerId,
  isArchived, setIsArchived,
  isViewMode,
  calendarItem, setCalendarItem,
  days, setDays, setNewDays,
  isAdminOfEventManager,
  refreshEventManager,
  setSelectedSectorId,
  showCharts,
  onToggleCharts
} ) => {

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const {
    onSectorChanged,
    onAxiosWarning,
    onAxiosError,
    eventManagers,
    setIsPopupVisible,
    isPopupVisible,
    isOpenVisualisationModal,
    setIsOpenVisualisationModal,
    onClickPreviousDays,
    onClickZoomIn,
    onClickZoomOut,
    canNavigateToFuture,
    onClickNextDays,
    onClickNavigationIndicators,
    updateTable,
    deleteTable
  } = useEventManagerNavigationBar(
    eventManager, onEventManagersChanged,
    eventManagerId,
    isArchived,
    isViewMode,
    calendarItem, setCalendarItem,
    days, setDays,
    refreshEventManager,
    setSelectedSectorId,
  );

  //---------------- Front ----------------\\
  return (
    <EventManagerNavigationBarSkeleton
      eventManagerId={ eventManagerId }
      isViewMode={ isViewMode }
      selectedClientIdKey={ KEY.SELECTED_CLIENT_ID }
      selectedSiteIdKey={ KEY.SELECTED_SITE_ID }
      selectedSectorIdKey={ KEY.SELECTED_SECTOR_ID }
      setIsPopupVisible={ setIsPopupVisible }
      isPopupVisible={ isPopupVisible }
      onSectorChanged={ onSectorChanged }
      updateTable={ updateTable }
      deleteTable={ deleteTable }
      onAxiosWarning={ onAxiosWarning }
      onAxiosError={ onAxiosError }
    >
      <div className='flex flex-wrap items-center w-full h-full'>
        <div className='flex items-center w-full'>

          <div className='flex gap-2 items-center shrink-0'>
            <HNBBreadcrumbItem
              icon={ EventManagerIcon }
              label={ eventManagers.find( e => {return e.id === eventManagerId;} )?.name || t( 'translation:no_event_manager' ) }
              data={ eventManagers.filter( item => { return item.id != eventManagerId; } ) }
              onItemSelect={ newEventManager => { setEventManagerId( newEventManager.id ); } }
            />
            {
              !isViewMode && isAdminOfEventManager && eventManager && (
                <>
                  <div id='event_manager_settings_icon'>
                    <Button
                      icon={ <GearIcon size={ 6 } className='group-hover:rotate-[360deg] trans-fast'/> }
                      className='flex p-2 gap-4 bg-orange-100 hover:bg-orange-200 text-orange-600 rounded-full shrink-0 mr-2 relative z-20'
                      onClick={() => {return setIsPopupVisible( !isPopupVisible );}}
                      tooltip={ t( 'translation:event_manager_settings' ) }
                    />
                  </div>
                  <div></div>
                  <TimerButton />
                </>
              )
            }
            {
              isArchived && (
                <span className='text-sm bg-purple-300 py-1 px-2 text-purple-700 border-2 border-purple-700 rounded-md w-fit h-fit'>
                  { t( 'translation:event_archived' ) }
                </span>
              )
            }
          </div>

          <div className="flex-1 flex items-center justify-center">
            {
              !isViewMode && (
                <>
                  <Button
                    icon={<EventManagerViewIcon className='h-9 w-9' />}
                    onClick={ onToggleCharts }
                    className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                    tooltip={ showCharts ? t( 'translation:event_manager' ) : t( 'translation:graphique' ) }
                  />
                </>
              )
            }
          </div>

          <div className={ `flex items-center gap-2 shrink-0 ${ !showCharts && days.length > 0 ? '' : 'invisible pointer-events-none' }` }>
            {
              days.length > 0 && (
                <>
                  <Button
                    icon={<ChevronsLeftIcon size={5} />}
                    onClick={ onClickPreviousDays }
                    className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                    tooltip={ t( 'translation:previous_range_days' ) }
                  />

                  {
                    !isViewMode && eventManagerId ?
                      <>
                        <Button
                          icon={<EyeIcon size={5} />}
                          onClick={ () => { setIsOpenVisualisationModal( true ); } }
                          className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                          tooltip={ t( 'translation:view_mode' ) }
                        />
                      </>
                      :
                      isViewMode && (
                        <>
                          <Button
                            icon={ <DisconnectIcon size={ 5 } /> }
                            onClick={ () => {
                              dispatch( clearVisualisationToken() );
                              CommonGlobalService.openAuthPage();
                            } }
                            className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                            tooltip={ t( 'common:disconnect' ) }
                          />
                        </>
                      )
                  }

                  <>
                    <Button
                      icon={<ArchiveBoxIcon className='h-5 w-5' />}
                      onClick={ () => { setIsArchived( !isArchived ); } }
                      className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                      tooltip={ isArchived ? t( 'translation:event_manager' ) : t( 'translation:archive_mode' ) }
                    />
                  </>
                  <>
                    <Button
                      icon={<MagnifyingGlassPlusIcon size={5} />}
                      onClick={ onClickZoomIn }
                      className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                      tooltip={ t( 'translation:zoom_in' ) }
                    />
                  </>
                  <>
                    <Button
                      icon={<MagnifyingGlassMinusIcon size={5} />}
                      onClick={ onClickZoomOut }
                      className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                      tooltip={ t( 'translation:zoom_out' ) }
                    />
                  </>

                  <DatePickerSoloInput
                    width='w-[150px]'
                    compact={false}
                    inputClassName='!border-0 !shadow-none !outline-none !ring-0 text-center bg-white h-[30px]'
                    shadow='shadow-none'
                    border='border border-gray-300 hover:border-gray-500 rounded-md'
                    defaultValue={ days[ 0 ].toDate() }
                    onChange={ newDate => { setNewDays( dayjs( newDate ) ); } }
                    minDate={ !isArchived ? dayjs().toDate() : undefined }
                    maxDate={ isArchived ? dayjs().startOf( 'day' ).toDate() : undefined }
                    customButtons={[
                      { label: t( 'common:today' ), date: dayjs().startOf( 'day' ) }
                    ]}
                  />

                  <div
                    className={ clsx(
                      'trans-fast',
                      !canNavigateToFuture()
                        ? 'invisible pointer-events-none'
                        : ''
                    ) }
                  >
                    <>
                      <Button
                        icon={<ChevronsRightIcon size={5} />}
                        onClick={ onClickNextDays }
                        className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                        tooltip={ t( 'translation:next_range_days' ) }
                      />
                    </>
                  </div>

                  {
                    !isViewMode && (
                      <>
                        <Button
                          icon={<ChartIcon size={5} />}
                          onClick={ onClickNavigationIndicators }
                          className='relative group rounded-md p-2 trans-fast bg-transparent text-black hover:text-black'
                          tooltip={ t( 'translation:indicator' ) }
                        />
                      </>
                    )
                  }
                </>
              )
            }
          </div>

        </div>
      </div>

      {
        eventManagerId && (
          <ActivationVisualisationModal
            isOpen={ isOpenVisualisationModal }
            onClose={ () => { setIsOpenVisualisationModal( false ); } }
            eventManagersId={ [ eventManagerId ] }
          />
        )
      }
    </EventManagerNavigationBarSkeleton>
  );
};